import { object, type InferOutput } from 'valibot'

import {
  type ShipmentOrderExEntity,
  type ShipmentOrdersListExEntity,
  type WorkType
} from '@/libs/api'

import {
  mapToShipmentOrderAdditionalFormValue,
  type ShipmentOrderAdditionalFormValue
} from './shipment-order-additional-form-value'
import {
  shipmentOrderCardEntries,
  updateOnlyEntries,
  withIncomeTaxRequiredChecks,
  withTimeRestrictionChecks
} from './shipment-order-card-schema'

/**
 * 配車前の配送依頼（shipmentOrder）編集用フォームの値
 * - 手動作成の依頼のみ対象
 */
const baseUpdateSchema = object({
  ...shipmentOrderCardEntries,
  ...updateOnlyEntries
})

export const updateNotDispatchedShipmentOrderFromValueSchema =
  withIncomeTaxRequiredChecks(withTimeRestrictionChecks(baseUpdateSchema))

/**
 * 配車前の配送依頼（shipmentOrder）編集用FormのDto
 */
export type UpdateNotDispatchedShipmentOrderFormDto = InferOutput<
  typeof updateNotDispatchedShipmentOrderFromValueSchema
>

/**
 * 配車前の配送依頼（shipmentOrder）編集用フォームの値
 */
export type UpdateNotDispatchedShipmentOrderFromValue = {
  /** 配送依頼ID。どの依頼を更新するかの識別子 */
  id: number
  customerId: number
  partnerId: number | null
  shipmentCategoryId: number
  originSiteId: number
  destSiteId: number
  originNote: string | null
  destNote: string | null
  originAreaId: number | null
  destAreaId: number | null
  waypoints: {
    siteId: number | null
    note: string | null
    areaId?: number | null
    workType?: WorkType | null
  }[]
  orderNo: string
  packageCategoryId: number | null
  note: string | null
  customKey1: string | null
  customKey2: string | null
  customKey3: string | null
  customKey4: string | null
  customKey5: string | null
  income: number | null
  incomeTaxCategoryId: number | null
  incomeIsTaxInclusive: boolean | null
  incomeRoundType: 'ROUND_UP' | 'ROUND_DOWN' | 'ROUND_HALF_UP' | null
  shippingDate: Date
  deliveryDate: Date
  incomeRecordingDate: Date
  departureTimeRestrictionStart: string | null
  departureTimeRestrictionEnd: string | null
  arrivalTimeRestrictionStart: string | null
  arrivalTimeRestrictionEnd: string | null
  isLoadingWorkOutsourced: boolean
  loadingWorkMinutes: number | null
  isUnloadingWorkOutsourced: boolean
  unloadingWorkMinutes: number | null
  loadingWorkNote: string | null
  unloadingWorkNote: string | null
  items: Item[]
  truckModelId: number | null
  additionalCharges: ShipmentOrderAdditionalFormValue[]
  // VOC_PJ-580: 編集画面からの履歴保存
  originHistoryId: number | null
  saveHistory: boolean
  historyId: number | null
  historyName: string | null
  saveMode?: 'overwrite' | 'new'
}

type Item = {
  detailId: number
  itemCode: string | null
  itemName: string | null
  packagedLoadQuantity: number | null
  packageCount: number | null
  quantity: number | null
  weight: number | null
}

/**
 * 配車前の配送依頼（shipmentOrder）編集用フォームの値に変換。
 *
 * 請求側 `additionalCharges` は list endpoint の include に含まれている
 * (T6 スコープ漏れ修正で追加済) ため、`shipmentOrder` から直接取り出して初期化する。
 * `additionals` 引数は後方互換のため残すが、未指定なら list entity 側を採用する。
 *
 * 支払側 (払い側) は #3541 指摘-8 で矢羽根 (DispatchDetail) 単位へ移行したため、
 * 配車前 (矢羽根未存在) の本フォームでは扱わない。
 */
export const mapToUpdateNotDispatchedShipmentOrderFormValue = ({
  shipmentOrder,
  additionals
}: {
  shipmentOrder: ShipmentOrdersListExEntity
  additionals?: Pick<ShipmentOrderExEntity, 'additionalCharges'>
}): UpdateNotDispatchedShipmentOrderFromValue => ({
  id: shipmentOrder.id,
  orderNo: shipmentOrder.orderNo,
  note: shipmentOrder.note ?? '',
  // #3463 VOC_PJ-829/851: カスタムキー1〜5の既存値を初期表示
  customKey1: shipmentOrder.customKey1 ?? null,
  customKey2: shipmentOrder.customKey2 ?? null,
  customKey3: shipmentOrder.customKey3 ?? null,
  customKey4: shipmentOrder.customKey4 ?? null,
  customKey5: shipmentOrder.customKey5 ?? null,
  income: shipmentOrder.income ?? null,
  // #3328 VOC_PJ-761: 既存の貰い運賃税区分を初期表示
  incomeTaxCategoryId: shipmentOrder.incomeTaxCategoryId ?? null,
  // VOC_PJ-844: 既存の貰い運賃内税フラグを初期表示（拠点デフォルト fallback は content 側で適用）
  incomeIsTaxInclusive: shipmentOrder.incomeIsTaxInclusive ?? null,
  // VOC_PJ-888 #3917: 既存の貰い運賃端数処理を初期表示（カスケード fallback は BE 側）
  incomeRoundType: shipmentOrder.incomeRoundType ?? null,
  partnerId: shipmentOrder.partnerId,
  shipmentCategoryId: shipmentOrder.group.shipmentCategoryId,
  customerId: shipmentOrder.group.customer.id,
  originSiteId: shipmentOrder.group.originSiteId,
  destSiteId: shipmentOrder.group.destSiteId,
  // 発着地備考は共有される group ではなく依頼直下に持つ（同じ発着地の他依頼へ漏らさない）
  originNote: shipmentOrder.originNote ?? null,
  destNote: shipmentOrder.destNote ?? null,
  // #4733: 保存済みのエリアを画面へ戻す
  originAreaId: shipmentOrder.originAreaId ?? null,
  destAreaId: shipmentOrder.destAreaId ?? null,
  // 経由地: 保存済みの { siteId, note } を行フォーム値へ復元（null は経由地なし）
  waypoints: (shipmentOrder.waypoints ?? []).map((waypoint) => ({
    siteId: waypoint.siteId,
    note: waypoint.note ?? null,
    // #4733: 保存済みの経由地エリアを画面へ戻す
    areaId: waypoint.areaId ?? null,
    // 作業内容（#4813）。応答に無ければ経由地の既定値
    workType: waypoint.workType ?? 'OTHER'
  })),
  packageCategoryId: shipmentOrder.group.packageCategoryId,
  shippingDate: new Date(shipmentOrder.group.shippingDate),
  deliveryDate: new Date(shipmentOrder.group.deliveryDate),
  // 編集モーダル: DB の既存値をそのまま初期表示（カスケード再計算しない）
  incomeRecordingDate: new Date(shipmentOrder.incomeRecordingDate),
  departureTimeRestrictionStart: shipmentOrder.departureTimeRestrictionStart,
  departureTimeRestrictionEnd: shipmentOrder.departureTimeRestrictionEnd,
  arrivalTimeRestrictionStart: shipmentOrder.arrivalTimeRestrictionStart,
  arrivalTimeRestrictionEnd: shipmentOrder.arrivalTimeRestrictionEnd,
  // FE は API より先にデプロイされるため(#4623)、応答に項目が無い窓では
  // undefined を既定の false に落とす（boolean 必須スキーマを壊さない）。
  isLoadingWorkOutsourced: shipmentOrder.isLoadingWorkOutsourced ?? false,
  loadingWorkMinutes: shipmentOrder.loadingWorkMinutes ?? null,
  isUnloadingWorkOutsourced: shipmentOrder.isUnloadingWorkOutsourced ?? false,
  unloadingWorkMinutes: shipmentOrder.unloadingWorkMinutes ?? null,
  loadingWorkNote: shipmentOrder.loadingWorkNote ?? null,
  unloadingWorkNote: shipmentOrder.unloadingWorkNote ?? null,
  items: shipmentOrder.items
    .slice()
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
    .map((item) => ({
      detailId: item.id,
      itemCode: item.itemCode,
      itemName: item.itemName,
      packagedLoadQuantity: item.packagedLoadQuantity,
      packageCount: item.packageCount,
      quantity: item.quantity,
      // 重量はkg単位で渡ってくるので注意
      weight: item.weight != null ? item.weight : null
    })),
  truckModelId: shipmentOrder.truckModelId ?? null,
  // VOC_PJ-580: 起点履歴ID（分岐A/B判定）
  originHistoryId: shipmentOrder.originHistoryId ?? null,
  saveHistory: false,
  historyId: shipmentOrder.originHistoryId ?? null,
  historyName: null,
  saveMode: shipmentOrder.originHistoryId != null ? 'overwrite' : undefined,
  // #1857 指摘-3: 付帯費用を include しない経路 (list endpoint の additionals 未取得 +
  // shipmentOrder 側も未定義) では undefined になりうるため、`?? []` でガードする。
  // 旧実装は両方 undefined のとき `.map` で TypeError となり編集モーダルがクラッシュしていた。
  additionalCharges: (
    additionals?.additionalCharges ??
    shipmentOrder.additionalCharges ??
    []
  ).map(mapToShipmentOrderAdditionalFormValue)
})
